/**
 * Post-load enrichment for terminal picking queue lists: GET /storage/tasks/sequence
 * with queue + state filters — all “open” sequence states except canceled / rejected / complete.
 * Only `routed_batch` queues are enriched (wave/active-sequence counts); other adapters skip the request.
 */

const statsCache = new Map()

/**
 * Sequence states to include in the “Активные” count (backend Orderadmin sequence states).
 * Excludes terminal states: canceled, rejected, complete.
 */
export const QUEUE_ENRICHMENT_SEQUENCE_STATES = [
  'queued',
  'new',
  'deleted',
  'processing',
  'pending'
]

/**
 * Minimal list query: matching sequences in this queue — only total_items is used.
 *
 * @param {string | number} queueId
 * @returns {object}
 */
export function buildSequenceEnrichmentQuery (queueId) {
  return {
    filter: [
      { type: 'eq', field: 'queue', value: queueId },
      { type: 'in', field: 'state', values: [...QUEUE_ENRICHMENT_SEQUENCE_STATES] }
    ],
    per_page: 1,
    page: 1
  }
}

function getQueueDataRef (wrapper) {
  if (wrapper && wrapper.data !== undefined && typeof wrapper.data === 'object') {
    return wrapper.data
  }
  return wrapper
}

function getSequenceStatsCached (sequenceService, queueId) {
  const key = String(queueId)
  if (statsCache.has(key)) {
    return statsCache.get(key)
  }
  const p = sequenceService.getAll(buildSequenceEnrichmentQuery(queueId))
    .then((data) => {
      const items = data.items || []
      const rawTotal = data.totalItems ?? data.total_items
      const total = rawTotal != null ? Number(rawTotal) : items.length
      return {
        _activeSequences: Number.isNaN(total) ? items.length : total
      }
    })
    .catch(() => ({
      _activeSequences: 0
    }))
  statsCache.set(key, p)
  return p
}

/**
 * @param {{ getAll: (query: object) => Promise<{ items?: object[], totalItems?: number, total_items?: number }> }} sequenceService — terminal `pickingSequence`
 * @param {Array<{ data?: object } | object>} wrappersOrRows — queue wrappers or raw queue rows
 * @returns {Promise<void>}
 */
export async function enrichPickingQueueWrappers (sequenceService, wrappersOrRows) {
  if (!Array.isArray(wrappersOrRows) || wrappersOrRows.length === 0) {
    return
  }

  const list = wrappersOrRows
  const jobs = list.map(async (item) => {
    const data = getQueueDataRef(item)
    const id = data && data.id
    if (id == null || id === '') {
      return
    }
    if (data.adapter !== 'routed_batch') {
      return
    }
    try {
      const stats = await getSequenceStatsCached(sequenceService, id)
      data._activeSequences = stats._activeSequences
    } catch {
      data._activeSequences = 0
    }
  })

  await Promise.all(jobs)
}
